import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Icon, type IconData } from '../primitives/icon.js';

export interface ModuleSummaryCardProps {
  title: string;
  icon: IconData;
  /** Module state, e.g. { tone: 'success', label: 'Activo' } — always text + icon. */
  status: { tone: StatusTone; label: string };
  /** Connection health summary, e.g. «1 conexión requiere atención». */
  health?: string | null;
  /** One actionable metric. `value: null` is shown as «Sin datos» (unknown is never 0). */
  metric?: { label: string; value: number | null } | null;
  /** Why the module is not ready (setup needed / unavailable). */
  reason?: string | null;
  /** Link to the module: pass a router Link (its children are replaced by the card content). */
  link: React.ReactElement<{ className?: string; children?: React.ReactNode }>;
  className?: string;
}

/** Compact module summary for Inicio: the whole card is one link to the module. */
export function ModuleSummaryCard({ title, icon, status, health, metric, reason, link, className }: ModuleSummaryCardProps) {
  const content = (
    <>
      <span className="flex items-start justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-control bg-accent-selected text-accent-on-selected">
            <Icon icon={icon} className="size-5" />
          </span>
          <span className="text-body-lg font-semibold text-text-primary">{title}</span>
        </span>
        <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
      </span>
      {reason ? <span className="text-body text-text-secondary">{reason}</span> : null}
      <span className="mt-auto grid gap-1">
        {metric ? (
          <span className="flex items-baseline justify-between gap-2">
            <span className="text-body text-text-secondary">{metric.label}</span>
            <span className="ef-tabular text-body-lg font-semibold text-text-primary">{metric.value === null ? 'Sin datos' : metric.value}</span>
          </span>
        ) : null}
        {health ? <span className="text-meta text-text-secondary">Conexiones: {health}</span> : null}
      </span>
    </>
  );
  return React.cloneElement(link, {
    className: cn(
      'flex min-h-40 flex-col gap-3 rounded-card border border-border-subtle bg-surface-default p-4 shadow-card transition-colors hover:border-border-control',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
      className,
    ),
    children: content,
  });
}
